import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { fetchAPI } from '../util/fetchAPI';
import { isNativeMobile } from '../config/api';
import { getStoreUrlForCurrentDevice } from '../util/platformUtils';

export interface VersionCheckResult {
  needsUpdate: boolean;
  currentVersion: string;
  minimumVersion: string;
  platform: 'ios' | 'android';
  storeUrl: string;
}

export class AppVersionService {
  private static instance: AppVersionService;

  private constructor() {}

  public static getInstance(): AppVersionService {
    if (!AppVersionService.instance) {
      AppVersionService.instance = new AppVersionService();
    }
    return AppVersionService.instance;
  }

  /**
   * Get the current app version from Capacitor
   */
  public async getCurrentVersion(): Promise<string | null> {
    if (!isNativeMobile()) {
      return null;
    }

    try {
      const appInfo = await App.getInfo();
      return appInfo.version;
    } catch (error) {
      console.error('Failed to get app version:', error);
      return null;
    }
  }

  /**
   * Get the current platform (ios or android)
   */
  public getPlatform(): 'ios' | 'android' | null {
    if (!isNativeMobile()) {
      return null;
    }

    const platform = Capacitor.getPlatform();
    if (platform === 'ios') {
      return 'ios';
    } else if (platform === 'android') {
      return 'android';
    }
    return null;
  }

  /**
   * Check if the app needs to be updated
   */
  public async checkVersion(): Promise<VersionCheckResult | null> {
    if (!isNativeMobile()) {
      return null;
    }

    const platform = this.getPlatform();
    const currentVersion = await this.getCurrentVersion();

    if (!platform || !currentVersion) {
      return null;
    }

    try {
      const apiUrl = `/api/app/version-check?platform=${platform}&version=${currentVersion}`;
      
      const response = await fetchAPI(
        apiUrl,
        { method: 'GET' },
        0 // No retries for version check
      );

      if (response.success && response.response) {
        // Backend returns snake_case, convert to camelCase
        const backendData = response.response as any;
        
        const data: VersionCheckResult = {
          needsUpdate: backendData.needs_update ?? false,
          currentVersion: backendData.current_version ?? '',
          minimumVersion: backendData.minimum_version ?? '',
          platform: backendData.platform ?? platform ?? 'android',
          storeUrl: backendData.store_url ?? '',
        };
        return data;
      } else {
        // On error, don't block the app - allow it to continue
        return null;
      }
    } catch (error) {
      console.error('Error checking app version:', error);
      // On error, don't block the app - allow it to continue
      return null;
    }
  }

  /**
   * Open the app store for the current platform.
   * Uses the same device check and store URLs as HomePage (platformUtils).
   */
  public async openAppStore(storeUrlFromApi?: string): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    // Same check and same URLs as HomePage so we open the correct store
    const urlToOpen = getStoreUrlForCurrentDevice() ?? storeUrlFromApi ?? '';
    if (!urlToOpen) {
      return;
    }

    try {
      window.open(urlToOpen, '_blank');
    } catch (error) {
      console.error('Failed to open app store:', error);
    }
  }
}

